import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { MeDto, Permission, SessionResponse } from "@tcr/contracts";
import { api, isApiError, setAccessToken, setRefreshHandler } from "@/api/client";
import { EP } from "@/api/endpoints";

type Status = "loading" | "anonymous" | "authenticated";

interface AuthContextValue {
  status: Status;
  me: MeDto | null;
  permissions: ReadonlySet<Permission>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Reload /auth/me (e.g. after an admin edits their own roles). */
  reloadMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Refresh this long before the access token expires. */
const REFRESH_LEAD_MS = 60_000;
const MIN_REFRESH_DELAY_MS = 5_000;

let refreshInFlight: Promise<SessionResponse> | null = null;

export function computeRefreshDelay(expiresAtIso: string, now = Date.now()): number {
  const exp = Date.parse(expiresAtIso);
  if (Number.isNaN(exp)) return 5 * 60_000;
  return Math.max(MIN_REFRESH_DELAY_MS, exp - now - REFRESH_LEAD_MS);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>("loading");
  const [me, setMe] = useState<MeDto | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const goAnonymous = useCallback(() => {
    clearTimer();
    setAccessToken(null);
    setMe(null);
    setStatus("anonymous");
    qc.clear();
  }, [qc]);

  // Forward-declared so applySession can schedule it.
  const refreshRef = useRef<() => Promise<string | null>>(async () => null);

  const applySession = useCallback(async (s: SessionResponse) => {
    setAccessToken(s.accessToken);
    clearTimer();
    timer.current = setTimeout(() => void refreshRef.current(), computeRefreshDelay(s.accessTokenExpiresAt));
    const m = await api.get<MeDto>(EP.me);
    setMe(m);
    setStatus("authenticated");
  }, []);

  const refresh = useCallback(async (): Promise<string | null> => {
    try {
      // Refresh tokens rotate: two concurrent refreshes (StrictMode double effects,
      // timer + 401 retry) could trip reuse detection, so share one in-flight call.
      refreshInFlight ??= api.post<SessionResponse>(EP.refresh, {}, { anonymous: true, noRetry: true }).finally(() => {
        refreshInFlight = null;
      });
      const s = await refreshInFlight;
      await applySession(s);
      return s.accessToken;
    } catch (e) {
      // Network blips must not log the user out; only an explicit auth failure does.
      if (isApiError(e, "NetworkError")) {
        clearTimer();
        timer.current = setTimeout(() => void refreshRef.current(), 10_000);
        return null;
      }
      goAnonymous();
      return null;
    }
  }, [applySession, goAnonymous]);
  refreshRef.current = refresh;

  useEffect(() => {
    setRefreshHandler(refresh);
    // Silent sign-in on load using the httpOnly refresh cookie.
    void refresh().then((t) => {
      if (!t) setStatus((s) => (s === "loading" ? "anonymous" : s));
    });
    return () => {
      setRefreshHandler(null);
      clearTimer();
    };
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const s = await api.post<SessionResponse>(EP.login, { email, password }, { anonymous: true, noRetry: true });
      await applySession(s);
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      await api.post(EP.logout, {}, { noRetry: true });
    } catch {
      // Logging out locally is what matters.
    }
    goAnonymous();
  }, [goAnonymous]);

  const reloadMe = useCallback(async () => {
    setMe(await api.get<MeDto>(EP.me));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, me, permissions: new Set(me?.permissions ?? []), login, logout, reloadMe }),
    [status, me, login, logout, reloadMe],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}

/** UI gating only — the server re-checks permission and resource scope on every call. */
export function useCan(...perms: Permission[]): boolean {
  const { permissions } = useAuth();
  return perms.every((p) => permissions.has(p));
}

export function Can({ perm, children, fallback = null }: { perm: Permission | Permission[]; children: ReactNode; fallback?: ReactNode }) {
  const { permissions } = useAuth();
  const list = Array.isArray(perm) ? perm : [perm];
  return <>{list.every((p) => permissions.has(p)) ? children : fallback}</>;
}
